import { apiRequest } from './apiRequest';
import { OP } from './operations';
import {
  activePipelineMatches,
  clearActivePipelineContext,
  getActivePipelineContext,
  setActivePipelineContext,
} from './pipelineContextStorage';
export const API_BASE_URL = 'https://calcx.integrumenergy.in/api';
//export const API_BASE_URL = 'http://localhost:8000';
//const API_BASE_URL = 'https://calcx.integrumenergy.in/api';

export {
  clearActivePipelineContext,
  getActivePipelineContext,
  setActivePipelineContext,
};

const emitGlobalParamsUpdated = (pipelineName, source = 'unknown') => {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('pipeline:global-params-updated', {
      detail: { pipelineName, source },
    }));
  } catch (e) {
    // no-op: avoid breaking API calls if event dispatch fails
  }
};

// Attach JWT token from localStorage to all fetch requests if present
// and auto-logout on 401 so that the user is routed back to the login screen.
const originalFetch = window.fetch.bind(window);

window.fetch = async (input, init = {}) => {
  const token = localStorage.getItem('pipeline_token');
  const activePipeline = getActivePipelineContext();
  const excludedPipelineContextPaths = new Set([
    '/create_new_empty_pipeline/',
    '/list_pipelines/',
    '/copy_pipeline/',
    '/legacy_pipelines/migration/list',
    '/legacy_pipelines/migration/copy',
    '/legacy_pipelines/migration/complete',
  ]);

  const finalInit = { ...(init || {}) };
  const headers = new Headers(finalInit.headers || {});
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  try {
    const requestUrl = typeof input === 'string' ? input : input?.url;
    const pathname = requestUrl ? new URL(requestUrl, window.location.origin).pathname : '';
    if (activePipeline.id && !headers.has('X-Pipeline-Id') && !excludedPipelineContextPaths.has(pathname)) {
      headers.set('X-Pipeline-Id', activePipeline.id);
    }
  } catch (e) {
    // Ignore URL parsing errors; the request can proceed without pipeline context.
  }
  finalInit.headers = headers;

  const response = await originalFetch(input, finalInit);

  if (response.status === 401 && token) {
    try {
      localStorage.removeItem('pipeline_token');
      clearActivePipelineContext();
    } catch (e) {
      // ignore storage errors
    }
    if (!window.__pipelineForcedLogout) {
      window.__pipelineForcedLogout = true;
      window.location.reload();
    }
  }

  return response;
};

// Login API: obtains JWT for an email/password account.
export const login = async (email, password) => {
  return await apiRequest({
    url: `${API_BASE_URL}/auth/login`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { email, password },
    operation: OP.auth.login,
    fetchFn: originalFetch,
  });
};

export const signup = async (email, password) => {
  return await apiRequest({
    url: `${API_BASE_URL}/auth/signup`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { email, password },
    operation: OP.auth.signup,
    fetchFn: originalFetch,
  });
};

export const getCurrentUser = async () => {
  return await apiRequest({
    url: `${API_BASE_URL}/auth/me`,
    operation: OP.auth.me,
    showSuccessToast: false,
  });
};

export const listMigrationPipelines = async () => {
  return await apiRequest({
    url: `${API_BASE_URL}/legacy_pipelines/migration/list`,
    operation: OP.auth.migrationList,
    showSuccessToast: false,
  });
};

export const copyMigrationPipelines = async (pipelineNames) => {
  return await apiRequest({
    url: `${API_BASE_URL}/legacy_pipelines/migration/copy`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { pipeline_names: pipelineNames },
    operation: OP.auth.migrationCopy,
  });
};

export const completePipelineMigration = async () => {
  return await apiRequest({
    url: `${API_BASE_URL}/legacy_pipelines/migration/complete`,
    method: 'POST',
    operation: OP.auth.migrationComplete,
  });
};

export const getUsageSummary = async (userId = null) => {
  return await apiRequest({
    url: `${API_BASE_URL}/usage/summary`,
    query: userId ? { user_id: userId } : undefined,
    operation: OP.auth.usageSummary,
    showSuccessToast: false,
  });
};

export const getAdminUsageUsers = async () => {
  return await apiRequest({
    url: `${API_BASE_URL}/usage/admin/users`,
    operation: OP.auth.adminUsageUsers,
    showSuccessToast: false,
  });
};

export const listAdminAccounts = async (status = 'pending') => {
  return await apiRequest({
    url: `${API_BASE_URL}/admin/accounts`,
    query: status ? { status } : undefined,
    operation: OP.auth.adminAccounts,
    showSuccessToast: false,
  });
};

export const approveAdminAccount = async (userId) => {
  return await apiRequest({
    url: `${API_BASE_URL}/admin/accounts/${encodeURIComponent(userId)}/approve`,
    method: 'POST',
    operation: OP.auth.adminApprove,
    showSuccessToast: false,
  });
};

export const rejectAdminAccount = async (userId) => {
  return await apiRequest({
    url: `${API_BASE_URL}/admin/accounts/${encodeURIComponent(userId)}/reject`,
    method: 'POST',
    operation: OP.auth.adminReject,
    showSuccessToast: false,
  });
};

export const sharePipeline = async (pipelineId, email, mode) => {
  return await apiRequest({
    url: `${API_BASE_URL}/pipelines/${encodeURIComponent(pipelineId)}/share`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { email, mode },
    operation: OP.pipeline.share,
    showSuccessToast: false,
  });
};

export const listPipelineShares = async (pipelineId) => {
  return await apiRequest({
    url: `${API_BASE_URL}/pipelines/${encodeURIComponent(pipelineId)}/shares`,
    operation: OP.pipeline.listShares,
    showSuccessToast: false,
  });
};

export const revokePipelineShare = async (pipelineId, userId) => {
  return await apiRequest({
    url: `${API_BASE_URL}/pipelines/${encodeURIComponent(pipelineId)}/shares/${encodeURIComponent(userId)}`,
    method: 'DELETE',
    operation: OP.pipeline.revokeShare,
    showSuccessToast: false,
  });
};
// Function to fetch document information by object ID
export const getDocumentById = async (objectId) => {
  return await apiRequest({
    url: `${API_BASE_URL}/get_document_by_id/`,
    query: { object_id: objectId },
    operation: OP.genUpload.getDocumentById,
  });
};

// Function to fetch pipeline data by name
export const fetchPipelineByName = async (pipelineName, options = {}) => {
  return await apiRequest({
    url: `${API_BASE_URL}/get_pipeline_by_name/${encodeURIComponent(pipelineName)}`,
    query: {
      ...(options.trackOpen ? { track_open: true } : {}),
      ...(options.pipelineId ? { pipeline_id: options.pipelineId } : activePipelineMatches(pipelineName) ? { pipeline_id: getActivePipelineContext().id } : {}),
    },
    operation: OP.pipeline.getByName,
  });
};

// Function to run a pipeline by name
export const runPipelineByName = async (pipelineName) => {
  return await apiRequest({
    url: `${API_BASE_URL}/run_saved_pipeline_by_name/${encodeURIComponent(pipelineName)}`,
    query: activePipelineMatches(pipelineName) ? { pipeline_id: getActivePipelineContext().id } : undefined,
    operation: OP.pipeline.runByName,
  });
};

// Function to create a new pipeline
export const createNewPipeline = async (pipelineName) => {
  return await apiRequest({
    url: `${API_BASE_URL}/create_new_empty_pipeline/`,
    method: 'POST',
    query: { pipeline_name: pipelineName },
    operation: OP.pipeline.create,
  });
};

// Function to list all pipelines
export const listPipelines = async () => {
  return await apiRequest({
    url: `${API_BASE_URL}/list_pipelines/`,
    operation: OP.pipeline.list,
  });
};

// Function to delete a pipeline by name
export const deletePipeline = async (pipelineName) => {
  return await apiRequest({
    url: `${API_BASE_URL}/delete_pipeline/${encodeURIComponent(pipelineName)}`,
    method: 'DELETE',
    query: activePipelineMatches(pipelineName) ? { pipeline_id: getActivePipelineContext().id } : undefined,
    operation: OP.pipeline.delete,
  });
};

// Function to get a pipeline by name and log the output
export const getPipelineByName = async (pipelineName, options = {}) => {
  return await apiRequest({
    url: `${API_BASE_URL}/get_pipeline_by_name/${encodeURIComponent(pipelineName)}`,
    query: {
      ...(options.trackOpen ? { track_open: true } : {}),
      ...(options.pipelineId ? { pipeline_id: options.pipelineId } : activePipelineMatches(pipelineName) ? { pipeline_id: getActivePipelineContext().id } : {}),
    },
    operation: OP.pipeline.getByName,
  });
};

// Function to copy an existing pipeline to a new pipeline
export const copyPipeline = async (existingPipelineName, newPipelineName) => {
  return await apiRequest({
    url: `${API_BASE_URL}/copy_pipeline/`,
    method: 'POST',
    query: {
      existing_pipeline_name: existingPipelineName,
      new_pipeline_name: newPipelineName,
      ...(activePipelineMatches(existingPipelineName) ? { existing_pipeline_id: getActivePipelineContext().id } : {}),
    },
    operation: OP.pipeline.copy,
  });
};

// Function to append an input node to a pipeline
export const appendInputNodeToPipeline = async (pipelineName, interval, nodeName, type, x = 0, y = 0) => {
  // Convert interval string to integer value (15 or 60) if it's a string
  let intervalValue = interval;
  if (typeof interval === 'string') {
    if (interval === '15 mins') intervalValue = 15;
    else if (interval === '1 hr') intervalValue = 60;
    else {
      const parsed = parseInt(interval, 10);
      intervalValue = isNaN(parsed) ? 15 : parsed;
    }
  }

  return await apiRequest({
    url: `${API_BASE_URL}/append_input_select_node_to_pipeline/`,
    query: {
      pipeline_Name: pipelineName,
      interval: intervalValue,
      node_name: nodeName,
      type,
      x,
      y,
    },
    operation: OP.node.appendInputSelect,
  });
};

// Function to view data at a specific node in a pipeline
export const view_node_ip_op = async (pipelineName, nodeName, runId = null) => {
  return await apiRequest({
    url: `${API_BASE_URL}/get_data_at_node/`,
    query: { pipeline_name: pipelineName, node_name: nodeName, ...(runId ? { run_id: runId } : {}) },
    operation: OP.node.getDataAtNode,
  });
};

// Function to download node output data as CSV
export const download_node_output = (pipelineName, nodeName, runId = null) => {
  const token = localStorage.getItem('pipeline_token');
  const tokenParam = token ? `&token=${encodeURIComponent(token)}` : '';
  const runParam = runId ? `&run_id=${encodeURIComponent(runId)}` : '';
  const pipelineIdParam = activePipelineMatches(pipelineName) ? `&pipeline_id=${encodeURIComponent(getActivePipelineContext().id)}` : '';
  const url = `${API_BASE_URL}/download_node_output/?pipeline_name=${pipelineName}&node_name=${nodeName}${runParam}${pipelineIdParam}${tokenParam}`;
  // Open the URL in a new tab to trigger the download
  window.open(url, '_blank');
};

export const downloadAnalysisWorkbook = (pipelineName = null, runId = null) => {
  const token = localStorage.getItem('pipeline_token');
  const params = new URLSearchParams();
  if (token) params.set('token', token);
  if (pipelineName) params.set('pipeline_name', pipelineName);
  if (runId) params.set('run_id', runId);
  if (pipelineName && activePipelineMatches(pipelineName)) params.set('pipeline_id', getActivePipelineContext().id);
  const query = params.toString();
  const url = `${API_BASE_URL}/download_analysis_excel/${query ? `?${query}` : ''}`;
  // Open the URL in a new tab to trigger the download
  window.open(url, '_blank');
};

export const exportSummaryPdf = async ({ html, filename }) => {
  const response = await apiRequest({
    url: `${API_BASE_URL}/export/summary-pdf`,
    method: 'POST',
    body: { html, filename },
    operation: OP.analysis.exportSummaryPdf,
    parseAs: 'raw',
    showSuccessToast: false,
  });
  return await response.blob();
};

// Function to get visualization data for a node
export const get_visualization_data = async (pipelineName, nodeName, yColumns, runId = null) => {
  return await apiRequest({
    url: `${API_BASE_URL}/get_visualization_data/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { pipeline_name: pipelineName, node_name: nodeName, y_columns: yColumns, ...(runId ? { run_id: runId } : {}) },
    operation: OP.node.getVisualizationData,
  });
};

// Get Lapsed sum from analysis CSV
export const getAnalysisLapsedSum = async (pipelineName = null, runId = null) => {
  return await apiRequest({
    url: `${API_BASE_URL}/analysis/lapsed_sum`,
    query: {
      ...(pipelineName ? { pipeline_name: pipelineName } : {}),
      ...(runId ? { run_id: runId } : {}),
    },
    operation: OP.analysis.lapsedSum,
  });
};

// Get analysis_step3_monthly with computed consumption
export const getAnalysisStep3Monthly = async (pipelineName = null, runId = null) => {
  return await apiRequest({
    url: `${API_BASE_URL}/analysis/step3_monthly`,
    query: {
      ...(pipelineName ? { pipeline_name: pipelineName } : {}),
      ...(runId ? { run_id: runId } : {}),
    },
    operation: OP.analysis.step3Monthly,
  });
};

// Get high-level banking summaries (TOD-wise and monthly)
export const getBankingSummary = async (pipelineName = null, runId = null) => {
  return await apiRequest({
    url: `${API_BASE_URL}/analysis/banking_summary`,
    query: {
      ...(pipelineName ? { pipeline_name: pipelineName } : {}),
      ...(runId ? { run_id: runId } : {}),
    },
    operation: OP.analysis.bankingSummary,
  });
};

// Get the Existing OA pre-matcher banking round's own TOD/monthly summary
// (separate from the proposal round returned by getBankingSummary above).
export const getExistingOaBankingSummary = async (pipelineName = null, runId = null) => {
  return await apiRequest({
    url: `${API_BASE_URL}/analysis/existing_oa_banking_summary`,
    query: {
      ...(pipelineName ? { pipeline_name: pipelineName } : {}),
      ...(runId ? { run_id: runId } : {}),
    },
    operation: OP.analysis.bankingSummary,
  });
};

// Get project assumptions sheet (for summary tab)
export const getProjectAssumptions = async (pipelineName = null, runId = null) => {
  return await apiRequest({
    url: `${API_BASE_URL}/analysis/project_assumptions`,
    query: {
      ...(pipelineName ? { pipeline_name: pipelineName } : {}),
      ...(runId ? { run_id: runId } : {}),
    },
    operation: OP.analysis.projectAssumptions,
  });
};

// Function to update a node's position
export const updateNodePosition = async (pipelineName, nodeName, x, y) => {
  return await apiRequest({
    url: `${API_BASE_URL}/update_node_position/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { pipeline_name: pipelineName, node_name: nodeName, x, y },
    operation: OP.node.updatePosition,
  });
};

// Function to upload data file for generation (test or actual via isTest)
export const uploadDataFile = async (
  type,
  makeAndModel,
  location,
  maxRange,
  startDate,
  endDate,
  interval,
  file,
  subLocation,
  poe,
  isTest = false
) => {
  let intervalValue;
  if (interval === '15 mins') intervalValue = 15;
  else if (interval === '1 hr') intervalValue = 60;
  else intervalValue = 15;

  if (typeof location === 'string' && location.startsWith('Test_')) {
    throw new Error(
      "Location must not start with 'Test_'. Use the Test / Actual selector instead."
    );
  }
  if (!subLocation || !String(subLocation).trim()) {
    throw new Error('Sub-location is required for new uploads.');
  }
  if (!poe || !String(poe).trim()) {
    throw new Error('PoE is required for new uploads.');
  }

  const formData = new FormData();
  formData.append('type', type);
  formData.append('makeAndModel', makeAndModel);
  formData.append('location', location);
  formData.append('sub_location', String(subLocation).trim());
  formData.append('poe', String(poe).trim());
  formData.append('maxRange', maxRange);
  formData.append('start_Date', startDate);
  formData.append('end_Date', endDate);
  formData.append('interval', intervalValue);
  formData.append('is_test', !!isTest);
  formData.append('file', file);

  return await apiRequest({
    url: `${API_BASE_URL}/gen_upload/`,
    method: 'POST',
    body: formData,
    operation: OP.genUpload.uploadDataFile,
  });
};

// Metadata-only update for an existing generation profile
export const editGenUploadMetadata = async (objectId, {
  type,
  makeAndModel,
  location,
  maxRange,
  startDate,
  endDate,
  interval,
  subLocation,
  poe,
}) => {
  let intervalValue;
  if (interval === '15 mins') intervalValue = 15;
  else if (interval === '1 hr') intervalValue = 60;
  else intervalValue = typeof interval === 'number' ? interval : parseInt(interval, 10) || 15;

  if (typeof location === 'string' && location.startsWith('Test_')) {
    throw new Error(
      "Location must not start with 'Test_'. Use the Test / Actual selector on upload instead."
    );
  }

  const formData = new FormData();
  formData.append('type', type);
  formData.append('makeAndModel', makeAndModel);
  formData.append('location', location);
  formData.append('maxRange', maxRange);
  formData.append('start_Date', startDate);
  formData.append('end_Date', endDate);
  formData.append('interval', intervalValue);
  if (subLocation !== undefined && subLocation !== null) {
    formData.append('sub_location', String(subLocation).trim());
  }
  if (poe !== undefined && poe !== null) {
    formData.append('poe', String(poe).trim());
  }

  return await apiRequest({
    url: `${API_BASE_URL}/gen_upload/${encodeURIComponent(objectId)}/`,
    method: 'PUT',
    body: formData,
    operation: OP.genUpload.editGenUploadMetadata,
  });
};



// Function to list makes and models by type
export const listMakesAndModelsByType = async (type, isTest = true) => {
  return await apiRequest({
    url: `${API_BASE_URL}/list_makes_and_models_by_type/`,
    query: { type, is_test: !!isTest },
    operation: OP.genUpload.listMakesAndModelsByType,
  });
};

// Function to list locations by type (location-first flow)
export const listLocationsByType = async (type, isTest = true) => {
  return await apiRequest({
    url: `${API_BASE_URL}/list_locations_by_type/`,
    query: { type, is_test: !!isTest },
    operation: OP.genUpload.listLocationsByType,
  });
};

// Function to list sub-locations by type and location
export const listSubLocationsByTypeAndLocation = async (type, location, isTest = true) => {
  return await apiRequest({
    url: `${API_BASE_URL}/list_sub_locations_by_type_and_location/`,
    query: { type, location, is_test: !!isTest },
    operation: OP.genUpload.listSubLocationsByTypeAndLocation,
  });
};

// Function to list makes and models by type and location (location-first flow)
export const listMakesAndModelsByTypeAndLocation = async (type, location, isTest = true, subLocation = undefined) => {
  return await apiRequest({
    url: `${API_BASE_URL}/list_makes_and_models_by_type_and_location/`,
    query: {
      type,
      location,
      is_test: !!isTest,
      ...(subLocation !== undefined && subLocation !== null ? { sub_location: subLocation } : {}),
    },
    operation: OP.genUpload.listMakesAndModelsByTypeAndLocation,
  });
};

// Function to list locations by type and make
export const listLocationsByTypeAndMake = async (type, makeAndModel, isTest = true) => {
  return await apiRequest({
    url: `${API_BASE_URL}/list_locations_by_type_and_make/`,
    query: { type, makeAndModel, is_test: !!isTest },
    operation: OP.genUpload.listLocationsByTypeAndMake,
  });
};

// Function to list PoE values in the cascade
export const listPoesByTypeLocationSubLocationAndMake = async (
  type,
  location,
  makeAndModel,
  isTest = true,
  subLocation = undefined
) => {
  return await apiRequest({
    url: `${API_BASE_URL}/list_poes_by_type_location_sub_location_and_make/`,
    query: {
      type,
      location,
      makeAndModel,
      is_test: !!isTest,
      ...(subLocation !== undefined && subLocation !== null ? { sub_location: subLocation } : {}),
    },
    operation: OP.genUpload.listPoesByTypeLocationSubLocationAndMake,
  });
};

// Function to list intervals by type, make and location (+ optional sub_location / poe)
export const listIntervalsByTypeMakeAndLocation = async (
  type,
  makeAndModel,
  location,
  isTest = true,
  subLocation = undefined,
  poe = undefined
) => {
  return await apiRequest({
    url: `${API_BASE_URL}/list_intervals_by_type_make_and_location/`,
    query: {
      type,
      makeAndModel,
      location,
      is_test: !!isTest,
      ...(subLocation !== undefined && subLocation !== null ? { sub_location: subLocation } : {}),
      ...(poe !== undefined && poe !== null ? { poe } : {}),
    },
    operation: OP.genUpload.listIntervalsByTypeMakeAndLocation,
  });
};





// Function to get all node positions for a pipeline
export const getNodePositions = async (pipelineName) => {
  return await apiRequest({
    url: `${API_BASE_URL}/get_node_positions/${encodeURIComponent(pipelineName)}`,
    operation: OP.node.getPositions,
  });
};

// Function to delete a node from a pipeline
export const deleteNodeFromPipeline = async (pipelineName, nodeName) => {
  return await apiRequest({
    url: `${API_BASE_URL}/delete_node_from_pipeline/`,
    method: 'DELETE',
    query: { pipeline_name: pipelineName, node_name: nodeName },
    operation: OP.node.deleteFromPipeline,
  });
};

// ---------- Global Params (Setup Utility) ----------
export const getGlobalParams = async (pipelineName) => {
  const data = await apiRequest({
    url: `${API_BASE_URL}/get_global_params/`,
    query: { pipeline_name: pipelineName },
    operation: OP.params.getGlobal,
  });
  return data?.global_params || {};
};

// ---------- Billing Analytics (UI persisted inputs) ----------
export const getBillingAnalyticsParams = async (pipelineName) => {
  const data = await apiRequest({
    url: `${API_BASE_URL}/get_billing_analytics_params/`,
    query: { pipeline_name: pipelineName },
    cache: 'no-store',
    operation: OP.params.getBillingAnalytics,
  });
  return data?.params || {};
};

export const setBillingAnalyticsParams = async (pipelineName, params) => {
  return await apiRequest({
    url: `${API_BASE_URL}/set_billing_analytics_params/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { pipeline_name: pipelineName, params: params || {} },
    operation: OP.params.setBillingAnalytics,
  });
};

export const setGlobalParam = async (pipelineName, key, value, propagate = true, apply_to_nodes = false) => {
  const res = await apiRequest({
    url: `${API_BASE_URL}/set_global_param/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { pipeline_name: pipelineName, key, value, propagate, apply_to_nodes },
    operation: OP.params.setGlobal,
  });
  emitGlobalParamsUpdated(pipelineName, 'setGlobalParam');
  return res;
};

// Function to reconfigure a node with a new input source
export const reconfigureNode = async (pipelineName, nodeName, newSourceNode, inputIndex = 0) => {
  return await apiRequest({
    url: `${API_BASE_URL}/reconfigure_node/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: {
      pipeline_name: pipelineName,
      node_name: nodeName,
      new_source_node: newSourceNode,
      input_index: inputIndex,
    },
    operation: OP.node.reconfigure,
  });
};

// Function to append a normalizer node to a pipeline
export const appendNormalizerNodeToPipeline = async (pipelineName, nodeName, target, inputNodeName, x = 0, y = 0) => {
  return await apiRequest({
    url: `${API_BASE_URL}/append_normalizer_node_to_pipeline/`,
    query: {
      pipeline_Name: pipelineName,
      node_name: nodeName,
      target,
      input_node_name: inputNodeName,
      x,
      y,
    },
    operation: OP.node.appendNormalizer,
  });
};

// Function to edit an existing normalizer node
export const editNormalizerNode = async (pipelineName, nodeName, newName, target, inputNodeName = null) => {
  const targetValue = typeof target === 'string' ? parseInt(target, 10) : target;

  return await apiRequest({
    url: `${API_BASE_URL}/edit_normalizer_node/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { pipeline_name: pipelineName, node_name: nodeName, target: targetValue },
    operation: OP.node.editNormalizer,
  });
};

// Function to edit an existing scaler node
export const editScalerNode = async (pipelineName, nodeName, multFactor, colName) => {
  const mult_factor = typeof multFactor === 'string' ? parseFloat(multFactor) : multFactor;

  const requestData = {
    pipeline_name: pipelineName,
    node_name: nodeName,
    mult_factor,
    ...(colName && colName !== 'Column name not specified' ? { col_name: colName } : {}),
  };

  const res = await apiRequest({
    url: `${API_BASE_URL}/edit_scaler_node/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: requestData,
    operation: OP.node.editScaler,
  });
  emitGlobalParamsUpdated(pipelineName, 'editScalerNode');
  return res;
};

// Function to edit a capper node's parameters
export const editCapperNode = async (
  pipelineName,
  nodeName,
  acdcRatio,
  colName,
  { KA = null, MH = null, genToTransLoss = null, capLimit = undefined } = {}
) => {
  const acdc_ratio = typeof acdcRatio === 'string' ? parseFloat(acdcRatio) : acdcRatio;

  const requestData = {
    pipeline_name: pipelineName,
    node_name: nodeName,
    acdc_ratio,
    ...(colName ? { col_name: colName } : {}),
    ...(KA !== null ? { KA: !!KA } : {}),
    ...(MH !== null ? { MH: !!MH } : {}),
    ...(genToTransLoss !== null
      ? { gen_to_trans_loss: typeof genToTransLoss === 'string' ? parseFloat(genToTransLoss) : genToTransLoss }
      : {}),
    ...(capLimit !== undefined
      ? {
          cap_limit:
            capLimit === null || capLimit === ''
              ? null
              : typeof capLimit === 'string'
                ? Number.isFinite(parseFloat(capLimit))
                  ? parseFloat(capLimit)
                  : null
                : Number.isFinite(capLimit)
                  ? capLimit
                  : null,
        }
      : {}),
  };

  return await apiRequest({
    url: `${API_BASE_URL}/edit_capper_node/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: requestData,
    operation: OP.node.editCapper,
  });
};

// Function to edit a finance node's parameters
export const editFinanceNode = async (
  pipelineName,
  nodeName,
  {
    unitsX = null,
    energyCostA = null,
    wheelingChargeB = null,
    electricityDutyC = null,
    todChargesD = null,
    taxE = null,
    facF = null,
    rebateG = null,
    discomTariffCalcMode = null,
    ghEnergyCharge = null,
    ghFuelSurcharge = null,
    ghPeakSurcharge = null,
    ghSolarPeakDiscount = null,
    ghPfIncentive = null,
    ghEhvRebate = null,
    ghDemandCharges = null,
    ghElectricityDuty = null,
    bulkConsumptionRebate = null,
    promptPaymentDiscount = null,
    incrementalConsumptionRebate = null,
    loadFactorIncentive = null,
    universalSubsidy = null,
    universalSubsidyLabel = null,
  } = {}
) => {
  const requestData = {
    pipeline_name: pipelineName,
    node_name: nodeName,
    ...(unitsX !== null ? { units_X: typeof unitsX === 'string' ? parseFloat(unitsX) : unitsX } : {}),
    ...(energyCostA !== null ? { energy_cost_A: typeof energyCostA === 'string' ? parseFloat(energyCostA) : energyCostA } : {}),
    ...(wheelingChargeB !== null ? { wheeling_charge_B: typeof wheelingChargeB === 'string' ? parseFloat(wheelingChargeB) : wheelingChargeB } : {}),
    ...(electricityDutyC !== null ? { electricity_duty_C: typeof electricityDutyC === 'string' ? parseFloat(electricityDutyC) : electricityDutyC } : {}),
    ...(todChargesD !== null ? { tod_charges_D: typeof todChargesD === 'string' ? parseFloat(todChargesD) : todChargesD } : {}),
    ...(taxE !== null ? { tax_E: typeof taxE === 'string' ? parseFloat(taxE) : taxE } : {}),
    ...(facF !== null ? { fac_F: typeof facF === 'string' ? parseFloat(facF) : facF } : {}),
    ...(rebateG !== null ? { rebate_G: typeof rebateG === 'string' ? parseFloat(rebateG) : rebateG } : {}),
    ...(discomTariffCalcMode !== null ? { discom_tariff_calc_mode: discomTariffCalcMode } : {}),
    ...(ghEnergyCharge !== null ? { gh_energy_charge: typeof ghEnergyCharge === 'string' ? parseFloat(ghEnergyCharge) : ghEnergyCharge } : {}),
    ...(ghFuelSurcharge !== null ? { gh_fuel_surcharge: typeof ghFuelSurcharge === 'string' ? parseFloat(ghFuelSurcharge) : ghFuelSurcharge } : {}),
    ...(ghPeakSurcharge !== null ? { gh_peak_surcharge: typeof ghPeakSurcharge === 'string' ? parseFloat(ghPeakSurcharge) : ghPeakSurcharge } : {}),
    ...(ghSolarPeakDiscount !== null ? { gh_solar_peak_discount: typeof ghSolarPeakDiscount === 'string' ? parseFloat(ghSolarPeakDiscount) : ghSolarPeakDiscount } : {}),
    ...(ghPfIncentive !== null ? { gh_pf_incentive: typeof ghPfIncentive === 'string' ? parseFloat(ghPfIncentive) : ghPfIncentive } : {}),
    ...(ghEhvRebate !== null ? { gh_ehv_rebate: typeof ghEhvRebate === 'string' ? parseFloat(ghEhvRebate) : ghEhvRebate } : {}),
    ...(ghDemandCharges !== null ? { gh_demand_charges: typeof ghDemandCharges === 'string' ? parseFloat(ghDemandCharges) : ghDemandCharges } : {}),
    ...(ghElectricityDuty !== null ? { gh_electricity_duty: typeof ghElectricityDuty === 'string' ? parseFloat(ghElectricityDuty) : ghElectricityDuty } : {}),
    ...(bulkConsumptionRebate !== null ? { bulk_consumption_rebate: typeof bulkConsumptionRebate === 'string' ? parseFloat(bulkConsumptionRebate) : bulkConsumptionRebate } : {}),
    ...(promptPaymentDiscount !== null ? { prompt_payment_discount: typeof promptPaymentDiscount === 'string' ? parseFloat(promptPaymentDiscount) : promptPaymentDiscount } : {}),
    ...(incrementalConsumptionRebate !== null
      ? { incremental_consumption_rebate: typeof incrementalConsumptionRebate === 'string' ? parseFloat(incrementalConsumptionRebate) : incrementalConsumptionRebate }
      : {}),
    ...(loadFactorIncentive !== null
      ? { load_factor_incentive: typeof loadFactorIncentive === 'string' ? parseFloat(loadFactorIncentive) : loadFactorIncentive }
      : {}),
    ...(universalSubsidy !== null ? { universal_subsidy: typeof universalSubsidy === 'string' ? parseFloat(universalSubsidy) : universalSubsidy } : {}),
    ...(universalSubsidyLabel !== null ? { universal_subsidy_label: universalSubsidyLabel } : {}),
  };

  return await apiRequest({
    url: `${API_BASE_URL}/edit_finance_node/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: requestData,
    operation: OP.node.editFinance,
  });
};

// Function to edit a matcher node's parameters
export const editAdderNode = async (pipelineName, nodeName, inputAColName, inputBColName, sumColumnName) => {
  return await apiRequest({
    url: `${API_BASE_URL}/edit_adder_node/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: {
      pipeline_name: pipelineName,
      node_name: nodeName,
      inputA_col_name: inputAColName,
      inputB_col_name: inputBColName,
      sum_column_name: sumColumnName,
    },
    operation: OP.node.editAdder,
  });
};

export const editMatcherNode = async (pipelineName, nodeName, consColName, genColName) => {
  const normalizeOptionalColumn = (value, sentinelLabel) => {
    if (value === undefined || value === null) return '';
    const trimmed = String(value).trim();
    if (!trimmed) return '';
    if (trimmed.toLowerCase() === sentinelLabel.toLowerCase()) return '';
    return String(value);
  };

  const normalizedCons = normalizeOptionalColumn(consColName, 'Consumption column not specified');
  const normalizedGen = normalizeOptionalColumn(genColName, 'Generation column not specified');

  return await apiRequest({
    url: `${API_BASE_URL}/edit_matcher_node/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: {
      pipeline_name: pipelineName,
      node_name: nodeName,
      ...(normalizedCons ? { cons_col_name: normalizedCons } : {}),
      ...(normalizedGen ? { gen_col_name: normalizedGen } : {}),
    },
    operation: OP.node.editMatcher,
  });
};

// Function to append a scaler node to a pipeline
export const appendScalerNodeToPipeline = async (pipelineName, nodeName, multFactor, colName, inputNodeName, x = 0, y = 0) => {
  const res = await apiRequest({
    url: `${API_BASE_URL}/append_scaler_node_to_pipeline/`,
    query: {
      pipeline_Name: pipelineName,
      node_name: nodeName,
      mult_factor: multFactor,
      col_name: colName || '',
      input_node_name: inputNodeName,
      x,
      y,
    },
    operation: OP.node.appendScaler,
  });
  emitGlobalParamsUpdated(pipelineName, 'appendScalerNodeToPipeline');
  return res;
};

// Function to append a finance node to a pipeline (cashEquity removed)
export const appendFinanceNodeToPipeline = async (
  pipelineName,
  nodeName,
  inputNodeName,
  { unitsX = 1, energyCostA = 0, wheelingChargeB = 0, electricityDutyC = 0, todChargesD = 0, taxE = 0, facF = 0, rebateG = 0 } = {},
  x = 0,
  y = 0
) => {
  return await apiRequest({
    url: `${API_BASE_URL}/append_finance_node_to_pipeline/`,
    method: 'POST',
    query: {
      pipeline_name: pipelineName,
      node_name: nodeName,
      input_node_name: inputNodeName,
      units_X: unitsX,
      energy_cost_A: energyCostA,
      wheeling_charge_B: wheelingChargeB,
      electricity_duty_C: electricityDutyC,
      tod_charges_D: todChargesD,
      tax_E: taxE,
      fac_F: facF,
      rebate_G: rebateG,
      x,
      y,
    },
    operation: OP.node.appendFinance,
  });
};

export const appendConsumptionNodeToPipeline = async (
  pipelineName,
  nodeName,
  file,
  x = 0,
  y = 0,
  {
    mode = 'hourly_csv',
    year = 2025,
    slotMonthlyData = null,
    bankingPolicyName = 'GH_2025',
    replaceExisting = false,
    existingOaReUnits = 0,
    existingBtmUnits = 0,
    existingOaType = 'solar',
    existingOaInputMode = 'annual',
    existingOaFile = null,
    existingOaFiles = [],
    existingOaBankingEnabled = false,
    existingOaBankingPolicy = '',
  } = {}
) => {
  const formData = new FormData();
  if (file) {
    formData.append('file', file);
  }
  if (existingOaFile) {
    formData.append('existing_oa_file', existingOaFile);
  }
  (existingOaFiles || []).forEach((oaFile) => {
    if (oaFile) formData.append('existing_oa_files', oaFile);
  });
  if (slotMonthlyData) {
    formData.append('slot_monthly_data', JSON.stringify(slotMonthlyData));
  }

  return await apiRequest({
    url: `${API_BASE_URL}/append_consumption_input_node/`,
    method: 'POST',
    query: {
      pipeline_name: pipelineName,
      node_name: nodeName,
      mode,
      year,
      banking_policy_name: bankingPolicyName,
      existing_oa_re_units: Number(existingOaReUnits || 0),
      existing_btm_units: Number(existingBtmUnits || 0),
      existing_oa_type: existingOaType || 'solar',
      existing_oa_input_mode: existingOaInputMode || 'annual',
      existing_oa_banking_enabled: Boolean(existingOaBankingEnabled),
      existing_oa_banking_policy: existingOaBankingPolicy || '',
      replace_existing: replaceExisting,
      x,
      y,
    },
    body: formData,
    operation: OP.node.appendConsumptionInput,
  });
};

export const updateMultiUnitOASetup = async (pipelineName, nodeName, units = [], balanceMode = 'calculated') => {
  return await apiRequest({
    url: `${API_BASE_URL}/update_multi_unit_oa_setup/`,
    method: 'POST',
    body: {
      pipeline_name: pipelineName,
      node_name: nodeName,
      units,
      balance_mode: balanceMode,
    },
    operation: OP.node.appendConsumptionInput,
  });
};

export const getBankingPolicySlots = async () => {
  return await apiRequest({
    url: `${API_BASE_URL}/banking_policy_slots/`,
    operation: OP.node.appendConsumptionInput,
  });
};

// Function to append a wind generation node to a pipeline
export const appendWindGenerationNodeToPipeline = async (
  pipelineName,
  nodeName,
  makeAndModel,
  location,
  interval,
  mwPerTurbine = 2.1,
  x = 0,
  y = 0,
  isTest = true,
  subLocation = undefined,
  poe = undefined
) => {
  let intervalValue = interval;
  if (typeof interval === 'string') {
    if (interval === '15 mins') intervalValue = 15;
    else if (interval === '1 hr') intervalValue = 60;
    else {
      const parsed = parseInt(interval, 10);
      intervalValue = isNaN(parsed) ? 15 : parsed;
    }
  }

  return await apiRequest({
    url: `${API_BASE_URL}/append_wind_generation_node_to_pipeline/`,
    query: {
      pipeline_Name: pipelineName,
      makeAndModel,
      location,
      interval: intervalValue,
      mw_per_turbine: mwPerTurbine,
      node_name: nodeName,
      is_test: !!isTest,
      x,
      y,
      ...(subLocation !== undefined && subLocation !== null ? { sub_location: subLocation } : {}),
      ...(poe !== undefined && poe !== null ? { poe } : {}),
    },
    operation: OP.node.appendWindGeneration,
  });
};

// Function to append a solar generation node to a pipeline
export const appendSolarGenerationNodeToPipeline = async (
  pipelineName,
  nodeName,
  makeAndModel,
  location,
  interval,
  btmFlag = false,
  x = 0,
  y = 0,
  isTest = true,
  subLocation = undefined,
  poe = undefined
) => {
  let intervalValue = interval;
  if (typeof interval === 'string') {
    if (interval === '15 mins') intervalValue = 15;
    else if (interval === '1 hr') intervalValue = 60;
    else {
      const parsed = parseInt(interval, 10);
      intervalValue = isNaN(parsed) ? 15 : parsed;
    }
  }

  return await apiRequest({
    url: `${API_BASE_URL}/append_solar_generation_node_to_pipeline/`,
    query: {
      pipeline_Name: pipelineName,
      makeAndModel,
      location,
      interval: intervalValue,
      Btm_flag: !!btmFlag,
      node_name: nodeName,
      is_test: !!isTest,
      x,
      y,
      ...(subLocation !== undefined && subLocation !== null ? { sub_location: subLocation } : {}),
      ...(poe !== undefined && poe !== null ? { poe } : {}),
    },
    operation: OP.node.appendSolarGeneration,
  });
};

export const appendCapperNodeToPipeline = async (
  pipelineName,
  nodeName,
  acdcRatio,
  colName,
  inputNodeName,
  x = 0,
  y = 0,
  options = {}
) => {
  const KA = options.KA !== undefined ? !!options.KA : false; // Karnataka
  const MH = options.MH !== undefined ? !!options.MH : true; // Maharashtra default
  const genToTransLoss =
    options.genToTransLoss !== undefined
      ? typeof options.genToTransLoss === 'string'
        ? parseFloat(options.genToTransLoss)
        : options.genToTransLoss
      : 0;

  const capLimit = options.capLimit !== undefined ? options.capLimit : undefined;
  let cap_limit;
  if (capLimit !== undefined && capLimit !== null && capLimit !== '') {
    const capNum = typeof capLimit === 'string' ? parseFloat(capLimit) : capLimit;
    cap_limit = Number.isFinite(capNum) ? capNum : undefined;
  }

  return await apiRequest({
    url: `${API_BASE_URL}/append_caper_node_to_pipeline/`,
    query: {
      pipeline_Name: pipelineName,
      node_name: nodeName,
      acdc_ratio: acdcRatio,
      col_name: colName || '',
      input_node_name: inputNodeName,
      KA,
      MH,
      gen_to_trans_loss: genToTransLoss,
      ...(cap_limit !== undefined ? { cap_limit } : {}),
      x,
      y,
    },
    operation: OP.node.appendCapper,
  });
};

export const appendAdderNodeToPipeline = async (
  pipelineName, 
  nodeName, 
  inputA_col_name, 
  inputB_col_name, 
  sum_column_name, 
  input_node_1_name, 
  input_node_2_name, 
  x = 0, 
  y = 0
) => {
  return await apiRequest({
    url: `${API_BASE_URL}/append_merger_node_to_pipeline/`,
    query: {
      pipeline_Name: pipelineName,
      node_name: nodeName,
      inputA_col_name: inputA_col_name || '',
      inputB_col_name: inputB_col_name || '',
      sum_column_name: sum_column_name || 'Total',
      input_node_1_name,
      input_node_2_name,
      x,
      y,
    },
    operation: OP.node.appendMerger,
  });
};

export const appendMatcherNodeToPipeline = async (
  pipelineName, 
  nodeName, 
  cons_col_name, 
  gen_col_name, 
  cons_node_name, 
  gen_node_name, 
  x = 0, 
  y = 0
) => {
  return await apiRequest({
    url: `${API_BASE_URL}/append_matcher_node_to_pipeline/`,
    query: {
      pipeline_Name: pipelineName,
      node_name: nodeName,
      cons_col_name: cons_col_name || '',
      gen_col_name: gen_col_name || '',
      cons_node_name,
      gen_node_name,
      x,
      y,
    },
    operation: OP.node.appendMatcher,
  });
};

export const appendLossNodeToPipeline = async (pipelineName, nodeName, transmissionLossPerc, wheelingLossPerc, colName, inputNodeName, x = 0, y = 0) => {
  return await apiRequest({
    url: `${API_BASE_URL}/append_loss_node_to_pipeline/`,
    query: {
      pipeline_Name: pipelineName,
      node_name: nodeName,
      transmission_loss_perc: transmissionLossPerc,
      wheeling_loss_perc: wheelingLossPerc,
      col_name: colName || '',
      input_node_name: inputNodeName,
      x,
      y,
    },
    operation: OP.node.appendLossSubtractor,
  });
};

export const editLossSubtractorNode = async (pipelineName, nodeName, transmissionLossPerc, wheelingLossPerc, colName = null) => {
  const requestData = {
    pipeline_name: pipelineName,
    node_name: nodeName,
    transmission_loss_perc: typeof transmissionLossPerc === 'string' ? parseFloat(transmissionLossPerc) : transmissionLossPerc,
    wheeling_loss_perc: typeof wheelingLossPerc === 'string' ? parseFloat(wheelingLossPerc) : wheelingLossPerc,
    ...(colName && colName !== 'Column name not specified' ? { col_name: colName } : {}),
  };

  return await apiRequest({
    url: `${API_BASE_URL}/edit_loss_subtractor_node/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: requestData,
    operation: OP.node.editLossSubtractor,
  });
};

export const appendExtrapolationNodeToPipeline = async (
  pipelineName, 
  nodeName, 
  years, 
  bankableYears, 
  inputNodeName, 
  windDepPerc = 0.0, 
  solarDepPerc = 0.5, 
  bankingCharge = 8, 
  totWindGenCol = '', 
  totSolarGenCol = '', 
  consAppPerc = 0.1, 
  tConsCol = '', 
  x = 0, 
  y = 0
) => {
  return await apiRequest({
    url: `${API_BASE_URL}/append_extrapolation_node_to_pipeline/`,
    query: {
      pipeline_Name: pipelineName,
      node_name: nodeName,
      input_node_name: inputNodeName,
      years,
      bankable_years: bankableYears,
      wind_dep_perc: windDepPerc,
      solar_dep_perc: solarDepPerc,
      banking_charge: bankingCharge,
      tot_wind_gen_col: totWindGenCol || '',
      tot_solar_gen_col: totSolarGenCol || '',
      cons_app_perc: consAppPerc,
      t_cons_col: tConsCol || '',
      x,
      y,
    },
    operation: OP.node.appendExtrapolation,
  });
};

export const editExtrapolationNode = async (
  pipelineName, 
  nodeName, 
  years, 
  bankableYears, 
  windDepPerc = 0.0, 
  solarDepPerc = 0.5, 
  bankingCharge = 8, 
  totWindGenCol = '', 
  totSolarGenCol = '', 
  consAppPerc = 0.1, 
  tConsCol = ''
) => {
  const requestBody = {
    pipeline_name: pipelineName,
    node_name: nodeName,
    years: years ? parseInt(years) : null,
    bankable_years: bankableYears ? parseInt(bankableYears) : null,
    wind_dep_perc: windDepPerc ? parseFloat(windDepPerc) : null,
    solar_dep_perc: solarDepPerc ? parseFloat(solarDepPerc) : null,
    banking_charge: bankingCharge ? parseFloat(bankingCharge) : null,
    tot_wind_gen_col: totWindGenCol || null,
    tot_solar_gen_col: totSolarGenCol || null,
    cons_app_perc: consAppPerc ? parseFloat(consAppPerc) : null,
    t_cons_col: tConsCol || null,
  };

  return await apiRequest({
    url: `${API_BASE_URL}/edit_extrapolation_node/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: requestBody,
    operation: OP.node.editExtrapolation,
  });
};

export const appendBankingNodeToPipeline = async (pipelineName, nodeName, bankingType, inputNodeName, x = 0, y = 0) => {
  return await apiRequest({
    url: `${API_BASE_URL}/append_banking_node_to_pipeline/`,
    query: {
      pipeline_Name: pipelineName,
      node_name: nodeName,
      banking_type: bankingType,
      input_node_name: inputNodeName,
      x,
      y,
    },
    operation: OP.node.appendBanking,
  });
};

// Add (or, with replaceExisting, edit) a Settle node: one existing generation
// source in a priority chain. `inputNodeName` is the Consumption node for the
// first source, otherwise the previous Settle node. `generationFile` is required
// for a new node and optional when editing.
export const appendSettleNodeToPipeline = async (
  pipelineName,
  nodeName,
  inputNodeName,
  {
    sourceLabel = '',
    bankingType = 'yearly_without_tod',
    bankingFee = 0.1,
    generationFile = null,
    replaceExisting = false,
    x = 0,
    y = 0,
  } = {}
) => {
  const formData = new FormData();
  if (generationFile) {
    formData.append('generation_file', generationFile);
  }
  return await apiRequest({
    url: `${API_BASE_URL}/append_settle_node_to_pipeline/`,
    method: 'POST',
    query: {
      pipeline_name: pipelineName,
      node_name: nodeName,
      input_node_name: inputNodeName,
      source_label: sourceLabel || nodeName,
      banking_type: bankingType,
      banking_fee: Number(bankingFee),
      replace_existing: replaceExisting,
      x,
      y,
    },
    body: formData,
    operation: OP.node.appendSettle,
  });
};

// Priority-ordered summary of a Settle chain (one block per source + combined totals).
export const getSourceSettlementSummary = async (pipelineName = null, runId = null) => {
  return await apiRequest({
    url: `${API_BASE_URL}/analysis/source_settlement_summary`,
    query: {
      ...(pipelineName ? { pipeline_name: pipelineName } : {}),
      ...(runId ? { run_id: runId } : {}),
    },
    operation: OP.analysis.sourceSettlementSummary,
  });
};

// Function to update banking type for an existing node
export const updateBankingType = async (pipelineName, nodeName, newBankingType) => {
  return await apiRequest({
    url: `${API_BASE_URL}/update_banking_type/`,
    query: { pipeline_name: pipelineName, node_name: nodeName, new_banking_type: newBankingType },
    operation: OP.node.updateBankingType,
  });
};

export const appendBatteryNodeToPipeline = async (pipelineName, nodeName, inputNodeName, params = {}, x = 0, y = 0) => {
  return await apiRequest({
    url: `${API_BASE_URL}/append_battery_node_to_pipeline/`,
    method: 'POST',
    headers: { accept: 'application/json', 'Content-Type': 'application/json' },
    query: {
      pipeline_name: pipelineName,
      node_name: nodeName,
      input_node_name: inputNodeName,
      gen_col_name: params.genColName || '',
      battery_hours: params.batteryHours,
      battery_capacity_kw: params.batteryCapacityKw,
      round_trip_efficiency: params.roundTripEfficiency,
      dod: params.dod,
      battery_capex_per_kwh: params.batteryCapexPerKwh,
      battery_om_perc: params.batteryOmPerc,
      loan_perc: params.loanPerc,
      annual_rate: params.annualRate,
      tenure_years: params.tenureYears,
      dep_rate_for_IT_WDV: params.depRateForITWDV,
      addn_dep_rate: params.addnDepRate,
      tax_rate: params.taxRate,
      gst_input: !!params.gstInput,
      gst_perc: params.gstPerc,
      charge_time_slots: JSON.stringify(params.chargeTimeSlots || []),
      discharge_time_slots: JSON.stringify(params.dischargeTimeSlots || []),
      x,
      y,
    },
    body: params.batteryAssumptions || { enabled: false },
    operation: OP.node.appendBattery,
  });
};

export const editBatteryNode = async (pipelineName, nodeName, params = {}) => {
  return await apiRequest({
    url: `${API_BASE_URL}/edit_battery_node/`,
    method: 'POST',
    headers: { accept: 'application/json', 'Content-Type': 'application/json' },
    body: {
      pipeline_name: pipelineName,
      node_name: nodeName,
      gen_col_name: params.genColName,
      battery_hours: params.batteryHours,
      battery_capacity_kw: params.batteryCapacityKw,
      round_trip_efficiency: params.roundTripEfficiency,
      dod: params.dod,
      battery_capex_per_kwh: params.batteryCapexPerKwh,
      battery_om_perc: params.batteryOmPerc,
      loan_perc: params.loanPerc,
      annual_rate: params.annualRate,
      tenure_years: params.tenureYears,
      dep_rate_for_IT_WDV: params.depRateForITWDV,
      addn_dep_rate: params.addnDepRate,
      tax_rate: params.taxRate,
      gst_input: params.gstInput,
      gst_perc: params.gstPerc,
      battery_assumptions: params.batteryAssumptions,
      charge_time_slots: params.chargeTimeSlots,
      discharge_time_slots: params.dischargeTimeSlots,
    },
    operation: OP.node.editBattery,
  });
};

// Function to append a loan node to a pipeline
export const appendLoanNodeToPipeline = async (pipelineName, nodeName, loanPerc, annualRate, tenureYears, moratoriumYears, x = 0, y = 0) => {
  return await apiRequest({
    url: `${API_BASE_URL}/append_loan_node_to_pipeline/`,
    method: 'POST',
    query: {
      pipeline_name: pipelineName,
      node_name: nodeName,
      loan_perc: loanPerc,
      annual_rate: annualRate,
      tenure_years: tenureYears,
      moratorium_years: moratoriumYears,
      x,
      y,
    },
    operation: OP.node.appendLoan,
  });
};

// Function to edit a loan node
export const editLoanNode = async (pipelineName, nodeName, loanPerc, annualRate, tenureYears, moratoriumYears) => {
  return await apiRequest({
    url: `${API_BASE_URL}/edit_loan_node/`,
    method: 'POST',
    headers: { accept: 'application/json', 'Content-Type': 'application/json' },
    body: {
      pipeline_name: pipelineName,
      node_name: nodeName,
      loan_perc: loanPerc,
      annual_rate: annualRate,
      tenure_years: tenureYears,
      moratorium_years: moratoriumYears,
    },
    operation: OP.node.editLoan,
  });
};

// Function to append a depreciation node to a pipeline
export const appendDepreciationNodeToPipeline = async (pipelineName, nodeName, BasicPriceWind, BasicPriceSolar, BasicPriceSolarBtm = 0, depRateForITWDV, addnDepRate, taxRate, gstInput = false, gstPerc = null, x = 0, y = 0) => {
  const addnNum = typeof addnDepRate === 'string' ? parseFloat(addnDepRate) : addnDepRate;
  const gstNum = typeof gstPerc === 'string' ? parseFloat(gstPerc) : gstPerc;
  const btmSolar =
    BasicPriceSolarBtm === null || BasicPriceSolarBtm === undefined || BasicPriceSolarBtm === '' || isNaN(BasicPriceSolarBtm)
      ? 0
      : BasicPriceSolarBtm;

  return await apiRequest({
    url: `${API_BASE_URL}/append_depreciation_node_to_pipeline/`,
    method: 'POST',
    query: {
      pipeline_name: pipelineName,
      node_name: nodeName,
      Basic_price_wind: BasicPriceWind,
      Basic_price_solar: BasicPriceSolar,
      Basic_price_solar_btm: btmSolar,
      dep_rate_for_IT_WDV: depRateForITWDV,
      ...(Number.isFinite(addnNum) ? { addn_dep_rate: addnNum } : {}),
      tax_rate: taxRate,
      gst_input: !!gstInput,
      ...(Number.isFinite(gstNum) ? { gst_perc: gstNum } : {}),
      x,
      y,
    },
    operation: OP.node.appendDepreciation,
  });
};

export const editDepreciationNode = async (pipelineName, nodeName, BasicPriceWind, BasicPriceSolar, BasicPriceSolarBtm = 0, depRateForITWDV, addnDepRate, taxRate, gstInput = null, gstPerc = null) => {
  const requestBody = {
    pipeline_name: pipelineName,
    node_name: nodeName,
    Basic_price_wind: BasicPriceWind,
    Basic_price_solar: BasicPriceSolar,
    Basic_price_solar_btm:
      BasicPriceSolarBtm === null || BasicPriceSolarBtm === undefined || BasicPriceSolarBtm === '' || isNaN(BasicPriceSolarBtm)
        ? 0
        : BasicPriceSolarBtm,
    dep_rate_for_IT_WDV: depRateForITWDV,
    addn_dep_rate: addnDepRate,
    tax_rate: taxRate,
    gst_input: gstInput,
    ...(gstPerc !== null && gstPerc !== undefined ? { gst_perc: gstPerc } : {}),
  };

  return await apiRequest({
    url: `${API_BASE_URL}/edit_depreciation_node/`,
    method: 'POST',
    headers: { accept: 'application/json', 'Content-Type': 'application/json' },
    body: requestBody,
    operation: OP.node.editDepreciation,
  });
};

// Edit wind generation node (update dataset selection)
export const editWindGenerationNode = async (
  pipelineName,
  nodeName,
  makeAndModel,
  location,
  interval,
  mwPerTurbine = 2.1,
  isTest = true,
  subLocation = undefined,
  poe = undefined
) => {
  const intervalValue =
    typeof interval === 'string'
      ? interval === '15 mins'
        ? 15
        : interval === '1 hr'
          ? 60
          : parseInt(interval, 10)
      : interval;

  return await apiRequest({
    url: `${API_BASE_URL}/edit_wind_generation_node/`,
    method: 'POST',
    headers: { accept: 'application/json', 'Content-Type': 'application/json' },
    body: {
      pipeline_name: pipelineName,
      node_name: nodeName,
      makeAndModel,
      location,
      interval: intervalValue,
      mw_per_turbine: mwPerTurbine,
      is_test: !!isTest,
      ...(subLocation !== undefined && subLocation !== null ? { sub_location: subLocation } : {}),
      ...(poe !== undefined && poe !== null ? { poe } : {}),
    },
    operation: OP.node.editWindGeneration,
  });
};

// Edit solar generation node (update dataset selection)
export const editSolarGenerationNode = async (
  pipelineName,
  nodeName,
  makeAndModel,
  location,
  interval,
  btmFlag = false,
  isTest = true,
  subLocation = undefined,
  poe = undefined
) => {
  const intervalValue =
    typeof interval === 'string'
      ? interval === '15 mins'
        ? 15
        : interval === '1 hr'
          ? 60
          : parseInt(interval, 10)
      : interval;

  return await apiRequest({
    url: `${API_BASE_URL}/edit_solar_generation_node/`,
    method: 'POST',
    headers: { accept: 'application/json', 'Content-Type': 'application/json' },
    body: {
      pipeline_name: pipelineName,
      node_name: nodeName,
      makeAndModel,
      location,
      interval: intervalValue,
      Btm_flag: !!btmFlag,
      is_test: !!isTest,
      ...(subLocation !== undefined && subLocation !== null ? { sub_location: subLocation } : {}),
      ...(poe !== undefined && poe !== null ? { poe } : {}),
    },
    operation: OP.node.editSolarGeneration,
  });
};

// Function to append an operational costs node to a pipeline
export const appendOperationalCostsNodeToPipeline = async (
  pipelineName, 
  nodeName, 
  inputExtrapolationNode,
  inputLoanNode,
  inputDepTaxNode,
  noOfBopYear,
  windBopEscalationPerc,
  windEscalationPerc,
  solarEscalationPerc,
  annualMaintenancePerTurbineInLakhs,
  annualMaintenancePerTurbineInLakhsWithBop,
  annualMaintenancePerSolarUnitInLakhs,
  insurancePercentOfAssetCost,
  operationalCharges,
  assetManagementFees,
  annualLeaseChargePerAcreSolar,
  annualLeaseChargePerAcreWind,
  annualLeaseEscalationPerc,
  solarAcresPerMWp,
  windAcresPerWTG,
  firstYearBenifits,
  revenueFromExcessBankedUnitsPerUnit,
  totalLapsedUnitsForExcessRevenuePerc,
  transmissionChargesPerKWh,
  wheelingCharges,
  electricityDutyPerUnit,
  perKW,
  perKWh,
  transmissionChargesPerKwPerMonth,
  discountRate,
  includeDepreciation = true,
  bankingChargeRs = 0,
  x = 0, 
  y = 0
) => {
  return await apiRequest({
    url: `${API_BASE_URL}/append_project_operational_cost_node_to_pipeline/`,
    query: {
      pipeline_Name: pipelineName,
      node_name: nodeName,
      input_extrapolation_node: inputExtrapolationNode,
      input_loan_node: inputLoanNode,
      input_dep_tax_node: inputDepTaxNode,
      no_of_bop_year: noOfBopYear,
      wind_bop_escalation_perc: windBopEscalationPerc,
      wind_escalation_perc: windEscalationPerc,
      solar_escalation_perc: solarEscalationPerc,
      annual_maintenance_per_turbine_in_lakhs: annualMaintenancePerTurbineInLakhs,
      annual_maintenance_per_turbine_in_lakhs_with_bop: annualMaintenancePerTurbineInLakhsWithBop,
      annual_maintenance_per_solar_unit_in_lakhs: annualMaintenancePerSolarUnitInLakhs,
      insurance_percent_of_asset_cost: insurancePercentOfAssetCost,
      operational_charges: operationalCharges,
      asset_management_fees: assetManagementFees,
      annual_lease_charge_per_acre_solar: annualLeaseChargePerAcreSolar,
      annual_lease_charge_per_acre_wind: annualLeaseChargePerAcreWind,
      annual_lease_escalation_perc: annualLeaseEscalationPerc,
      solar_acres_per_MWp: solarAcresPerMWp,
      wind_acres_per_WTG: windAcresPerWTG,
      first_year_benifits: firstYearBenifits,
      revenue_from_excess_banked_units_per_unit: revenueFromExcessBankedUnitsPerUnit,
      total_lapsed_units_for_excess_revenue_perc: totalLapsedUnitsForExcessRevenuePerc,
      transmission_charges_per_KWh: transmissionChargesPerKWh,
      wheeling_charges: wheelingCharges,
      electricity_duty_per_unit: electricityDutyPerUnit,
      per_KW: perKW,
      per_KWh: perKWh,
      transmission_charges_per_Kw_per_month: transmissionChargesPerKwPerMonth,
      discount_rate: discountRate,
      include_depreciation: includeDepreciation,
      banking_charge_rs: bankingChargeRs ?? 0,
      x,
      y,
    },
    operation: OP.node.appendOperationalCosts,
  });
};

// Function to edit an operational costs node
export const editOperationalCostsNode = async (
  pipelineName, 
  nodeName, 
  noOfBopYear,
  windBopEscalationPerc,
  windEscalationPerc,
  solarEscalationPerc,
  annualMaintenancePerTurbineInLakhs,
  annualMaintenancePerTurbineInLakhsWithBop,
  annualMaintenancePerSolarUnitInLakhs,
  insurancePercentOfAssetCost,
  operationalCharges,
  assetManagementFees,
  annualLeaseChargePerAcreSolar,
  annualLeaseChargePerAcreWind,
  annualLeaseEscalationPerc,
  solarAcresPerMWp,
  windAcresPerWTG,
  firstYearBenifits,
  revenueFromExcessBankedUnitsPerUnit,
  totalLapsedUnitsForExcessRevenuePerc,
  transmissionChargesPerKWh,
  wheelingCharges,
  electricityDutyPerUnit,
  perKW,
  perKWh,
  transmissionChargesPerKwPerMonth,
  discountRate,
  includeDepreciation = null,
  bankingChargeRs = null
) => {
  const requestData = {
    pipeline_name: pipelineName,
    node_name: nodeName,
    no_of_bop_year: noOfBopYear,
    wind_bop_escalation_perc: windBopEscalationPerc,
    wind_escalation_perc: windEscalationPerc,
    solar_escalation_perc: solarEscalationPerc,
    annual_maintenance_per_turbine_in_lakhs: annualMaintenancePerTurbineInLakhs,
    annual_maintenance_per_turbine_in_lakhs_with_bop: annualMaintenancePerTurbineInLakhsWithBop,
    annual_maintenance_per_solar_unit_in_lakhs: annualMaintenancePerSolarUnitInLakhs,
    insurance_percent_of_asset_cost: insurancePercentOfAssetCost,
    operational_charges: operationalCharges,
    asset_management_fees: assetManagementFees,
    annual_lease_charge_per_acre_solar: annualLeaseChargePerAcreSolar,
    annual_lease_charge_per_acre_wind: annualLeaseChargePerAcreWind,
    annual_lease_escalation_perc: annualLeaseEscalationPerc,
    solar_acres_per_MWp: solarAcresPerMWp,
    wind_acres_per_WTG: windAcresPerWTG,
    first_year_benifits: firstYearBenifits,
    revenue_from_excess_banked_units_per_unit: revenueFromExcessBankedUnitsPerUnit,
    total_lapsed_units_for_excess_revenue_perc: totalLapsedUnitsForExcessRevenuePerc,
    transmission_charges_per_KWh: transmissionChargesPerKWh,
    wheeling_charges: wheelingCharges,
    electricity_duty_per_unit: electricityDutyPerUnit,
    ...(bankingChargeRs !== null && bankingChargeRs !== undefined ? { banking_charge_rs: bankingChargeRs } : {}),
    per_KW: perKW,
    per_KWh: perKWh,
    transmission_charges_per_Kw_per_month: transmissionChargesPerKwPerMonth,
    discount_rate: discountRate,
    ...(includeDepreciation !== null ? { include_depreciation: !!includeDepreciation } : {}),
  };

  return await apiRequest({
    url: `${API_BASE_URL}/edit_project_operational_cost_node/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: requestData,
    operation: OP.node.editOperationalCosts,
  });
};

export const getScenarioMetrics = async () => apiRequest({
  url: `${API_BASE_URL}/scenario-metrics`,
  operation: OP.scenario.metrics,
  showSuccessToast: false,
});

export const createScenarioSweep = async (configuration) => apiRequest({
  url: `${API_BASE_URL}/scenario-sweeps`,
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: configuration,
  operation: OP.scenario.create,
});

export const getScenarioSweep = async (sweepId) => apiRequest({
  url: `${API_BASE_URL}/scenario-sweeps/${encodeURIComponent(sweepId)}`,
  operation: OP.scenario.status,
  showSuccessToast: false,
});

export const getScenarioResults = async (sweepId, { page = 1, pageSize = 500, metricIds = [] } = {}) => apiRequest({
  url: `${API_BASE_URL}/scenario-sweeps/${encodeURIComponent(sweepId)}/results`,
  query: {
    page,
    page_size: pageSize,
    ...(metricIds.length ? { metric_ids: metricIds.join(',') } : {}),
  },
  operation: OP.scenario.results,
  showSuccessToast: false,
});

export const cancelScenarioSweep = async (sweepId) => apiRequest({
  url: `${API_BASE_URL}/scenario-sweeps/${encodeURIComponent(sweepId)}/cancel`,
  method: 'POST',
  operation: OP.scenario.cancel,
});

export const resumeScenarioSweep = async (sweepId) => apiRequest({
  url: `${API_BASE_URL}/scenario-sweeps/${encodeURIComponent(sweepId)}/resume`,
  method: 'POST',
  operation: OP.scenario.resume,
});

export const applyScenarioCandidate = async (sweepId, candidateId) => apiRequest({
  url: `${API_BASE_URL}/scenario-sweeps/${encodeURIComponent(sweepId)}/apply/${encodeURIComponent(candidateId)}`,
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: { confirmed: true },
  operation: OP.scenario.apply,
});

export const solveMultiUnitOptimization = async (request) => apiRequest({
  url: `${API_BASE_URL}/multi-unit-optimization/solve`,
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: request,
  operation: OP.multiUnitOptimization.solve,
});
